import { Phone, MessageCircle, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TEL_HREF, WHATSAPP_HREF, PHONE_DISPLAY } from "@/lib/ascentix";

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden grain-bg"
    >
      <div className="absolute inset-0 amber-glow pointer-events-none" />

      <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* LEFT */}
          <div className="lg:col-span-7 reveal">
            <span
              data-testid="hero-eyebrow"
              className="eyebrow inline-flex items-center gap-2"
            >
              <Sparkles className="size-3.5 text-orange-600" />
              Professional Training & Leadership Development
            </span>

            <h1
              data-testid="hero-headline"
              className="font-display mt-5 text-[42px] sm:text-6xl lg:text-[72px] xl:text-[78px] leading-[1.02] text-slate-900"
            >
              <span className="block">Equipping</span>
              <span className="block whitespace-nowrap">
                <span className="text-orange-600">Professionals</span> for
              </span>
              <span className="block relative">
                Peak Performance
                <span className="absolute left-0 right-0 -bottom-2 h-[6px] rounded-full bg-gradient-to-r from-orange-500 via-amber-500 to-amber-300/40" />
              </span>
              <span className="sr-only">.</span>
            </h1>

            <p
              data-testid="hero-subheadline"
              className="mt-7 max-w-xl text-[17px] md:text-[18px] leading-relaxed text-slate-600"
            >
              Leadership, business development and digital capability training
              for organisations ready to build stronger teams, sharper managers
              and customer-centred growth.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Button
                data-testid="hero-cta-book"
                onClick={() => scrollTo("contact")}
                className="rounded-full bg-orange-600 hover:bg-orange-700 text-white h-12 px-6 text-[15px] font-semibold shadow-[0_14px_30px_-12px_rgba(234,88,12,0.65)]"
              >
                Book a Consultation
                <ArrowRight className="ml-2 size-4" />
              </Button>
              <a
                data-testid="hero-cta-whatsapp"
                href={WHATSAPP_HREF}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 h-12 px-5 rounded-full border border-slate-900/10 bg-white hover:border-orange-600 hover:text-orange-600 transition-colors text-[15px] font-medium"
              >
                <MessageCircle className="size-4" />
                WhatsApp Us
              </a>
              <a
                data-testid="hero-cta-call"
                href={TEL_HREF}
                className="inline-flex items-center gap-2 h-12 px-5 rounded-full text-slate-800 hover:text-orange-600 transition-colors text-[15px] font-medium"
              >
                <Phone className="size-4" />
                Call {PHONE_DISPLAY}
              </a>
            </div>

            <div className="mt-10 flex items-center gap-5 text-sm text-slate-500">
              <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-white border border-slate-900/10">
                <span className="size-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-[13px] font-medium text-slate-700">
                  Booking Q1 cohorts
                </span>
              </div>
              <span className="hidden sm:inline">
                Trusted by banks, ministries, chambers of commerce & corporates
                across Nigeria.
              </span>
            </div>
          </div>

          {/* RIGHT visual */}
          <div className="lg:col-span-5 reveal" style={{ transitionDelay: "120ms" }}>
            <div className="relative">
              {/* Decorative ascent peaks */}
              <div className="absolute -top-6 -left-6 w-44 h-44 pointer-events-none opacity-90">
                <svg viewBox="0 0 200 200" className="w-full h-full">
                  <defs>
                    <linearGradient id="g1" x1="0" y1="1" x2="0" y2="0">
                      <stop offset="0%" stopColor="#fbbf24" />
                      <stop offset="100%" stopColor="#ea580c" />
                    </linearGradient>
                  </defs>
                  <polygon points="100,10 180,180 20,180" fill="url(#g1)" opacity="0.18" />
                  <polygon points="100,40 160,180 40,180" fill="url(#g1)" opacity="0.28" />
                  <polygon points="100,70 140,180 60,180" fill="url(#g1)" opacity="0.6" />
                </svg>
              </div>

              <div className="relative rounded-[28px] overflow-hidden border border-slate-900/10 bg-white shadow-[0_30px_80px_-30px_rgba(15,23,42,0.35)]">
                <img
                  data-testid="hero-image"
                  src="https://images.pexels.com/photos/5439381/pexels-photo-5439381.jpeg?auto=compress&cs=tinysrgb&w=1400"
                  alt="Black African and colleague closing a business agreement in a boardroom"
                  className="w-full h-[460px] md:h-[520px] object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />

                {/* Floating stat card */}
                <div className="absolute left-5 bottom-5 right-5 md:right-auto md:max-w-[300px] backdrop-blur-xl bg-white/85 border border-white/60 rounded-2xl p-4 shadow-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center shrink-0">
                      <span className="font-display text-orange-600 text-[17px]">120+</span>
                    </div>
                    <div>
                      <div className="text-[13px] font-semibold text-slate-900">
                        Years' Combined Experience
                      </div>
                      <div className="text-[12px] text-slate-500">
                        Across leadership & enterprise growth
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom-right accent badge */}
              <div className="absolute -right-3 -bottom-3 hidden md:flex items-center gap-2 px-4 py-2 bg-slate-900 text-amber-100 rounded-full shadow-lg text-[13px] font-medium">
                <span className="size-1.5 rounded-full bg-orange-400 animate-pulse" />
                Now booking Q1 cohorts
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
